import { fmt2, fmtEUR } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, MapPin } from 'lucide-react';

const AGE_BUCKETS = [
  { value: '0_90', label: '0-90 Tage' },
  { value: '91_180', label: '91-180 Tage' },
  { value: '181_360', label: '181-360 Tage' },
  { value: 'gt_360', label: '>360 Tage' },
];

const UOM_OPTIONS_DEFAULT = ['kg', 'sqm', 'pce', 'm', 'rolls', 'pallets', 'tons'];

export const ExistingSkuList = ({ skus, t }) => (
  <Card className="mb-6" data-testid="existing-skus-card">
    <CardHeader>
      <CardTitle className="text-lg">
        {t('create.existingSkus') || 'Vorhandene Bestande'} ({skus.length})
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {skus.map((sku) => (
          <div key={sku.sku_id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div>
                <p className="font-medium text-sm">{sku.article_number}</p>
                {sku.erp_article_number && (
                  <p className="text-xs text-muted-foreground">ERP: {sku.erp_article_number}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {sku.site?.site_name || sku.site_id}
                </div>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div className="text-sm">
                <span className="font-medium">{fmt2(sku.quantity)} {sku.uom}</span>
                {sku.list_price_kEUR && (
                  <span className="ml-3 text-orange-500 font-bold">
                    {fmtEUR(sku.list_price_kEUR)} EUR
                  </span>
                )}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${
              sku.status === 'available' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              sku.status === 'documentation_missing' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
              'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
            }`}>
              {t(`status.${sku.status}`) || sku.status}
            </span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const SkuFormSection = ({ skus, sites, isEditMode, isExternal, onUpdateField, onSiteChange, onAdd, onRemove, units, t }) => (
  <Card className="mb-6" data-testid="new-sku-card">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="flex items-center gap-2 text-lg">
        <MapPin className="h-5 w-5 text-orange-500" />
        {isEditMode ? (t('create.addSku') || 'Neuen Bestand hinzufugen') : (t('create.skuInfo') || 'Bestandsdaten (SKU)')}
      </CardTitle>
      {(skus.length > 0 || isEditMode) && (
        <Button variant="outline" size="sm" onClick={onAdd} data-testid="add-sku-button">
          <Plus className="h-4 w-4 mr-1" />
          {t('create.addMore') || 'Weitere SKU'}
        </Button>
      )}
    </CardHeader>
    <CardContent className="space-y-6">
      {skus.map((sku, index) => (
        <div key={index} className="relative p-4 border rounded-lg bg-slate-50/50 dark:bg-slate-900/30" data-testid={`sku-form-${index}`}>
          {skus.length > 1 && (
            <Button variant="ghost" size="icon"
              className="absolute top-2 right-2 h-7 w-7 text-slate-400 hover:text-red-500"
              onClick={() => onRemove(index)} data-testid={`remove-sku-${index}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {skus.length > 1 && <p className="text-xs text-muted-foreground mb-3 font-medium">SKU #{index + 1}</p>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t('filter.site') || 'Standort'} *</Label>
              <Select value={sku.site_id} onValueChange={(v) => onSiteChange ? onSiteChange(index, v) : onUpdateField(index, 'site_id', v)} disabled={isExternal}>
                <SelectTrigger data-testid={`sku-site-select-${index}`}>
                  <SelectValue placeholder={t('create.selectSite') || 'Standort wahlen'} />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(s => (
                    <SelectItem key={s.site_id} value={s.site_id}>
                      {s.site_name} ({s.city}, {s.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rollennummer / Lagerplatz *</Label>
              <Input value={sku.article_number} onChange={(e) => onUpdateField(index, 'article_number', e.target.value)}
                placeholder="z.B. R-001 oder LP-A12" data-testid={`sku-article-input-${index}`} />
            </div>
            <div>
              <Label>{t('create.erpArticleNumber') || 'ERP-Artikelnummer'}</Label>
              <Input value={sku.erp_article_number || ''} onChange={(e) => onUpdateField(index, 'erp_article_number', e.target.value)}
                placeholder="z.B. ERP-12345" data-testid={`sku-erp-article-input-${index}`} />
            </div>
            <div>
              <Label>{t('request.quantity') || 'Menge'} *</Label>
              <div className="flex gap-2">
                <Input type="number" value={sku.quantity} onChange={(e) => onUpdateField(index, 'quantity', e.target.value)}
                  placeholder="1000" className="flex-1" data-testid={`sku-quantity-input-${index}`} />
                <Select value={sku.uom} onValueChange={(v) => onUpdateField(index, 'uom', v)}>
                  <SelectTrigger className="w-24" data-testid={`sku-uom-select-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(units && units.length > 0 ? units.map(u => u.code || u) : UOM_OPTIONS_DEFAULT).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('create.rolls') || 'Anzahl Rollen'}</Label>
              <Input type="number" value={sku.number_of_rolls} onChange={(e) => onUpdateField(index, 'number_of_rolls', e.target.value)}
                placeholder="10" data-testid={`sku-rolls-input-${index}`} />
            </div>
            <div>
              <Label>{t('create.productionDate') || 'Produktionsdatum'}</Label>
              <Input type="date" value={sku.production_date} onChange={(e) => onUpdateField(index, 'production_date', e.target.value)}
                data-testid={`sku-prod-date-input-${index}`} />
            </div>
            <div>
              <Label>{t('create.bookValue') || 'Buchwert'} (EUR)</Label>
              <Input type="number" step="0.01" value={sku.book_value_EUR} onChange={(e) => onUpdateField(index, 'book_value_EUR', e.target.value)}
                placeholder="5500" data-testid={`sku-book-value-input-${index}`} />
            </div>
            <div>
              <Label>{t('create.listPrice') || 'Listenpreis'} (EUR)</Label>
              <Input type="number" step="0.01" value={sku.list_price_EUR} onChange={(e) => onUpdateField(index, 'list_price_EUR', e.target.value)}
                placeholder="7000" data-testid={`sku-list-price-input-${index}`} />
            </div>
            <div className="flex items-center gap-6 md:col-span-3 pt-2">
              <div className="flex items-center gap-2">
                <Switch checked={sku.customer_specific} onCheckedChange={(v) => onUpdateField(index, 'customer_specific', v)}
                  data-testid={`sku-customer-specific-${index}`} />
                <Label className="text-sm">{t('create.customerSpecific') || 'Kundenspezifisch'}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={sku.compliance_flag} onCheckedChange={(v) => onUpdateField(index, 'compliance_flag', v)}
                  data-testid={`sku-compliance-${index}`} />
                <Label className="text-sm">{t('create.compliant') || 'Konform'}</Label>
              </div>
            </div>
            <div className="md:col-span-3">
              <Label>{t('create.notesInternal') || 'Interne Notizen'}</Label>
              <Textarea value={sku.notes_internal} onChange={(e) => onUpdateField(index, 'notes_internal', e.target.value)}
                placeholder={t('create.notesInternalPlaceholder') || 'Nur intern sichtbar...'} rows={2}
                data-testid={`sku-notes-internal-${index}`} />
            </div>
          </div>
        </div>
      ))}

      {skus.length === 0 && isEditMode && (
        <div className="text-center py-6 text-muted-foreground">
          <p className="mb-3">{t('create.noNewSkus') || 'Keine neuen Bestande hinzugefugt'}</p>
          <Button variant="outline" onClick={onAdd} data-testid="add-first-sku-button">
            <Plus className="h-4 w-4 mr-1" />
            {t('create.addSku') || 'Bestand hinzufugen'}
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);
